/**
 * Converted Modal Component
 */

import React, { useMemo, useState } from 'react';
import { Modal, Form, Input, Radio, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useQueries } from '@tanstack/react-query';
import { useCurrentYearHolidays, useBufferDaysRules } from '@/features/delivery-settings/hooks';
import { DEFAULT_BUFFER_DAYS } from '@/features/delivery-settings/constants';
import { isDeliveryDateDisabled, toHolidaySet } from '@/features/delivery-settings/utils/deliveryDate';
import { getHighestBufferDays } from '@/features/delivery-settings/utils/bufferDays';
import { useGetQuoteLineItems } from '@/features/quotes-management/hooks';
import { getSubQuoteProductsApi } from '@/features/quotes-management/api/dataverse';
import { reconstructAssistantMessages } from '@/features/quotes-management/utils/transformSubQuoteProducts';
import { queryKey } from '@/features/quotes-management/constants';

interface ConvertedModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (formData: { remarks?: string; destination: 'email' | 'sms'; requestdeliveryby: string }) => void;
  isLoading?: boolean;
  showPaymentDestination?: boolean;
  quoteId?: string;
  region?: number | null;
}

export const ConvertedModal: React.FC<ConvertedModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  showPaymentDestination = false,
  quoteId,
  region = null,
}) => {
  const [form] = Form.useForm();
  const [destination, setDestination] = useState<'email' | 'sms'>('email');

  // Calendar resolved by current-year match (self-healing — see
  // useCurrentYearHolidays) rather than a stored activeCalendarId setting,
  // which 404s forever once the calendar it points to is deleted/recreated.
  const { holidays } = useCurrentYearHolidays();
  const { data: bufferDaysRules = [] } = useBufferDaysRules();
  const holidayDates = toHolidaySet(holidays);
  const conversionDate = dayjs();

  // Reconstruct each window item's answers from its sub-products so buffer-day
  // rules (region/product/colour/spray/security scoped) can be matched per
  // item — mirrors react-web's SignQuoteDrawer. Only fetched while the modal
  // is open, since this is otherwise unused (no point fetching line items for
  // every quote row just to have this modal mounted and ready).
  const { data: lineItemsData } = useGetQuoteLineItems(open ? quoteId : undefined);
  const lineItems = lineItemsData?.value ?? [];
  const subProductsQueries = useQueries({
    queries: (open ? lineItems : []).map((item) => ({
      queryKey: [queryKey.getSubQuoteProducts, item.quotedetailid],
      queryFn: () => getSubQuoteProductsApi(item.quotedetailid),
      enabled: open && !!item.quotedetailid,
      staleTime: 2 * 60 * 1000,
    })),
  });
  const itemsMessages = useMemo(
    () => subProductsQueries.map((q) => reconstructAssistantMessages((q.data?.value ?? []) as any[])),
    [subProductsQueries],
  );

  const bufferDays = useMemo(
    () => getHighestBufferDays(itemsMessages, bufferDaysRules, region, DEFAULT_BUFFER_DAYS),
    [itemsMessages, bufferDaysRules, region],
  );

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      onConfirm({
        remarks: values.remarks,
        destination,
        requestdeliveryby: (values.requestdeliveryby as Dayjs).format('YYYY-MM-DD'),
      });
      form.resetFields();
      setDestination('email');
    } catch (error) {
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setDestination('email');
    onClose();
  };

  return (
    <Modal
      title="Place Order"
      open={open}
      onCancel={handleCancel}
      onOk={handleConfirm}
      okText="Confirm"
      cancelText="Cancel"
      confirmLoading={isLoading}
      width={600}
    >
      <p className="text-gray-600 mb-4">
        Convert this quote to an order
      </p>

      <Form form={form} layout="vertical">
        {showPaymentDestination && (
          <Form.Item label="Send Payment Link Via">
            <Radio.Group
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
            >
              <Radio value="email">Email</Radio>
              <Radio value="sms">SMS</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        <Form.Item
          label="Expected Delivery Date"
          name="requestdeliveryby"
          rules={[{ required: true, message: 'Please select an expected delivery date' }]}
          help={`Weekends, bank holidays and the ${bufferDays}-working-day lead time are unavailable`}
        >
          <DatePicker
            className="w-full"
            format="DD/MM/YYYY"
            placeholder="Select delivery date"
            disabledDate={(date) => isDeliveryDateDisabled(date, conversionDate, bufferDays, holidayDates)}
          />
        </Form.Item>

        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea
            rows={4}
            placeholder="Enter any remarks about the conversion"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ConvertedModal;
